import React from 'react';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { TaxUtils, Constants } from '@bingads-webui-clientcenter/entity-utils';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import ValidationLegacyStrings from '@bingads-webui-clientcenter/validation-legacy-strings';
import { FilePickerStringMap } from '@bingads-webui-react/file-picker-string-map';
import { FilePickerProgressStringMap } from '@bingads-webui-react/file-picker-progress-string-map';
import { TaxTypeFormPropTypes } from './shapes';
import { DefaultViewTaxForm } from './taxforms/viewtaxforms/default-viewtaxform';
import { BaseViewTaxForm } from './taxforms/viewtaxforms/base-viewtaxform';
import { IsTaxExemptionCertVerifiedTypeKey } from './constants';

const {
  TaxType, TaxExemptionCertVerifiedResult, AttestationValue,
} = Constants;
const {
  defaultsTaxDataWithEmptyValue, getTaxTypeLabel, normalizeTaxExemptionCertVerifiedResult, normalizeAttestationValue, getTaxTypeTaxOptionMap,
} = TaxUtils;

export const getValidationI18n = i18n => buildLegacyI18n(i18n, ValidationLegacyStrings);
export const getFilePickerProgressI18n = i18n => buildLegacyI18n(
  i18n,
  {
    ...FilePickerStringMap,
    ...FilePickerProgressStringMap,
  }
);

export { getTaxTypeLabel };

export const withViewTaxFormWithFilledEmptyTaxValue = ({
  ViewTaxForm = DefaultViewTaxForm,
  getTaxSchema,
  countryCode,
}) => {
  const HOC = (props) => {
    const taxSchema = getTaxSchema(props);
    return (
      <ViewTaxForm
        {...props}
        taxSchema={taxSchema}
        filledData={defaultsTaxDataWithEmptyValue({
          taxInfoUIObject: props.data,
          taxSchema,
        })}
        countryCode={countryCode}
      />
    );
  };

  HOC.propTypes = TaxTypeFormPropTypes;
  HOC.displayName = `withViewTaxFormWithFilledEmptyTaxValue$(${getDisplayName(ViewTaxForm)})`;

  hoistNonReactStatics(HOC, ViewTaxForm);

  return HOC;
};

export const getPilotStatus = (pilot, permissions) => !!_.chain(permissions).result('dynamic').result(pilot).value();

export const addLegalIdentifierNumberInTaxSchema = (taxSchema) => {
  // eslint-disable-next-line no-param-reassign
  taxSchema.properties[TaxType.LegalIdentifier] = {
    type: 'string',
    required: true,
  };
};

export const addTaxApprovalFormInTaxSchema = (taxSchema) => {
  // eslint-disable-next-line no-param-reassign
  taxSchema.properties[IsTaxExemptionCertVerifiedTypeKey] = {
    type: 'string',
    required: false,
  };
};

export const getUSTaxFromWithStateCode = (USTaxform, stateCode) => {
  const HOC = props => <USTaxform {...props} stateCode={stateCode} />;

  return HOC;
};

export const getTaxOptionType = ({ uiObject, permissions }) => {
  const TaxOptionMapping = getTaxTypeTaxOptionMap(permissions); // get different tax type to tax option mapping
  const TaxOptionTypeKey = _.findKey(uiObject, (taxValue, taxType) => TaxType[taxType] && TaxOptionMapping[taxType]); // filter the key that are not supported in the mapping

  return TaxOptionMapping[TaxOptionTypeKey];
};


export const getHelpPopupTopicFromTaxSchema = ({ taxSchema, taxType }) =>
  _.result(taxSchema.properties[taxType], 'helpPopupTopic');

export const BaseViewTaxFormList = ({
  i18n, uiObject, taxSchema, vatStatus, countryCode, permissions = {},
}) => (
  <>
    {_.map(uiObject, (taxValue, taxType) => (
      <BaseViewTaxForm
        i18n={i18n}
        key={taxType}
        name={taxType}
        label={getTaxTypeLabel(taxType, i18n, { countryCode }, permissions)}
        value={taxValue}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType })}
        vatStatus={vatStatus}
      />
    ))}
  </>
);

export const isTaxExemptionCertVerified = isTaxExemptionCertVerifiedMTValue =>
  normalizeTaxExemptionCertVerifiedResult(isTaxExemptionCertVerifiedMTValue) === TaxExemptionCertVerifiedResult.Accept;

export const shouldDisplayTaxCertifications = taxCertifications => !!_.result(taxCertifications[0], 'displayName');
export const isConfirmedYes = mtValue => normalizeAttestationValue(mtValue) === AttestationValue.True;
export const isConfirmedNo = mtValue => normalizeAttestationValue(mtValue) === AttestationValue.False;

export const withTaxFormByPilot = ({
  pilotFlag,
  PilotForm,
  DefaultForm,
}) => {
  const HOC = (props) => {
    if (!_.chain(props.permissions).result('dynamic').result(pilotFlag).value()) {
      return <DefaultForm {...props} />;
    }

    return <PilotForm {...props} />;
  };

  HOC.propTypes = TaxTypeFormPropTypes;
  HOC.displayName = `withTaxFormByPilot$(${getDisplayName(PilotForm)})`;

  hoistNonReactStatics(HOC, PilotForm);

  return HOC;
};

export const onTaxFormValidateFillingLegalIdentifier = ({
  newTaxData, onValidate, prevTaxData, listenedToField, visuallyHidden,
}) => {
  const newValue = _.get(newTaxData.data, listenedToField);

  if (!visuallyHidden) {
    // if listenedToField is changed, we need to update LegalIdentifier as well
    const prevValue = _.get(prevTaxData, listenedToField);
    onValidate(newValue !== prevValue ? { ...newTaxData, data: { ...newTaxData.data, [TaxType.LegalIdentifier]: newValue } } : newTaxData);
  } else {
    // if legal identifier is visually hidden, we need to align it with the value of listenedToField
    const newLegalIdentifierValue = _.get(newTaxData.data, TaxType.LegalIdentifier);
    onValidate(newLegalIdentifierValue !== newValue ? { ...newTaxData, data: { ...newTaxData.data, [TaxType.LegalIdentifier]: newValue } } : newTaxData);
  }
};
